/* eslint-disable indent */
/**
 * Represents an Artikl.
 * @typedef {Object} Artikl
 * @property {string} id
 * @property {string} naziv
 * @property {number} kolicina
 * @property {string} mjernaJedinica
 * @property {string} mjernaJedinicaId
 * @property {number} jedinicnaCijenaBezPdv
 * @property {number} ukupnaCijenaBezPdv
 * @property {number} stopaPdv
 * @property {number} iznosPdv
 * @property {number} rabat
 * @property {number} netoUkupnaCijenaBezPdva
 * @property {number} ukupnaCijenaSPdv
 * @property {number} jedinicnaCijenaSPdv
 * @property {object} item
 */

export class Artikl {
  // eslint-disable-next-line space-before-function-paren
  constructor(artikl = {}) {
    this.setId(artikl?.id ?? '')
    this.setNaziv(artikl?.artikl ?? '')
    this.setKolicina(artikl?.kolicina ?? 0)
    this.setJedinicnaCijenaBezPdv(artikl?.jedinicnaCijenaBezPdv ?? 0)
    this.setUkupnaCijenaBezPdv(artikl?.ukupnaCijenaBezPdv ?? 0)
    this.setStopaPdv(artikl?.stopaPdv ?? 0)
    this.setIznosPdv(artikl?.iznosPdv ?? 0)
    this.setRabat(artikl?.rabat ?? 0)
    this.setNetoUkupnaCijenaBezPdva(artikl?.jedinicnaCijenaSRabatom ?? 0)
    this.setUkupnaCijenaSPdv(artikl?.ukupnaCijenaSPdv ?? 0)
    this.setJedinicnaCijenaSPdv(artikl?.jedinicnaCijenaSPdv ?? 0)
    this.setMjernaJedinicaID(artikl?.mjernaJedinica ?? '')
    this.setItem(artikl?.item ?? {})
  }

  setId (id) {
    this.id = id
  }

  setNaziv (naziv) {
    this.naziv = naziv
  }

  setKolicina (kolicina) {
    if (kolicina) {
      this.kolicina = kolicina * 1000
    } else {
      this.kolicina = kolicina
    }
  }

  setJedinicnaCijenaBezPdv (jedinicnaCijenaBezPdv) {
    this.jedinicnaCijenaBezPdv = parseInt(
      (jedinicnaCijenaBezPdv * 100000).toFixed(2)
    )
  }

  setUkupnaCijenaBezPdv (ukupnaCijenaBezPdv) {
    this.ukupnaCijenaBezPdv = parseInt((ukupnaCijenaBezPdv * 10000).toFixed(2))
  }

  setStopaPdv (stopaPdv) {
    this.stopaPdv = stopaPdv * 100
  }

  setIznosPdv (iznosPdv) {
    this.iznosPdv = parseInt((iznosPdv * 100).toFixed(2))
  }

  setRabat (rabat) {
    this.rabat = rabat * 100
  }

  setNetoUkupnaCijenaBezPdva (netoUkupnaCijenaBezPdva) {
    this.netoUkupnaCijenaBezPdva = parseInt(
      (netoUkupnaCijenaBezPdva * 100).toFixed(2)
    )
  }

  setUkupnaCijenaSPdv (ukupnaCijenaSPdv) {
    this.ukupnaCijenaSPdv = parseInt((ukupnaCijenaSPdv * 100).toFixed(2))
  }

  setJedinicnaCijenaSPdv (jedinicnaCijenaSPdv) {
    this.jedinicnaCijenaSPdv = parseInt((jedinicnaCijenaSPdv * 100).toFixed(2))
  }

  setMjernaJedinicaID (mjernaJedinicaId) {
    this.mjernaJedinicaId = mjernaJedinicaId
  }

  setItem (item) {
    this.item = item
  }
}

/**
 * Represents a Primka.
 * @typedef {Object} Primka
 * @property {string} dobavljac
 * @property {string} brojPrimke - Broj temelja (broj primke dobavljača)
 * @property {string} interni_broj_primke - Interni broj primke (iz delivery_counter)
 * @property {Artikl[]} artikli
 * @property {string} nacinPlacanja
 * @property {number} ukupanIznosPdv
 * @property {number} ukupanIznosBezPdv
 * @property {number} ukupanIznosSPdv
 * @property {number} ukupnoPovratnaNaknada
 * @property {object} porezi
 */

/** @type {Primka} */
export class Primka {
  // eslint-disable-next-line space-before-function-paren
  constructor() {
    this.idSkladista = ''
    this.nazivSkladista = ''
    this.idLokacije = ''
    this.prodajnoSkladiste = false
    this.idFirme = ''
    this.dobavljac = ''
    this.brojPrimke = '' // Broj temelja
    this.interni_broj_primke = '' // Interni broj primke (iz delivery_counter)
    this.artikli = []
    this.nacinPlacanja = ''
    this.ukupanIznosPdv = 0
    this.ukupanIznosBezPdv = 0
    this.ukupanIznosSPdv = 0
    this.ukupnoPovratnaNaknada = 0
    this.datum = ''
    this.ukupnoPovratnaNaknada = 0
    this.akcija = ''
    this.tipDokumenta = ''
    this.tipRabata = 0
    this.tipPoreza = 0
    this.povezanaPrimka = ''
    this.porezi = []
  }

  setDatum (datum) {
    this.datum = parseInt((datum / 1000).toFixed(2))
  }

  setDobavljac (dobavljac) {
    this.dobavljac = dobavljac
  }

  setIdFirme (firma) {
    this.idFirme = firma
  }

  setIdSkladista (skladiste) {
    this.idSkladista = skladiste
  }

  setNazivSkladista (naziv) {
    this.nazivSkladista = naziv
  }

  setBrojPrimke (brojPrimke) {
    this.brojPrimke = brojPrimke
  }

  setInterniBrojPrimke (interniBrojPrimke) {
    this.interni_broj_primke = interniBrojPrimke
  }

  setNacinPlacanja (nacinPlacanja) {
    this.nacinPlacanja = nacinPlacanja
  }

  setUkupanIznosPdv (ukupanIznosPdv) {
    this.ukupanIznosPdv = parseInt((ukupanIznosPdv * 100).toFixed(2))
  }

  setUkupanIznosBezPdv (ukupanIznosBezPdv) {
    this.ukupanIznosBezPdv = parseInt((ukupanIznosBezPdv * 100).toFixed(2))
  }

  setUkupanIznosSPdv (ukupanIznosSPdv) {
    this.ukupanIznosSPdv = parseInt((ukupanIznosSPdv * 100).toFixed(2))
  }

  setUkupnoPovratnaNaknada (ukupnoPovratnaNaknada) {
    this.ukupnoPovratnaNaknada = this.getUkupnoPovratnaNaknada()
  }

  setIdLokacije (idLokacije) {
    this.idLokacije = idLokacije
  }

  setProdajnoSkladiste (prodajnoSkladiste) {
    this.prodajnoSkladiste = prodajnoSkladiste
  }

  setAkcija (akcija) {
    this.akcija = akcija
  }

  setTipDokumenta (tipDokumenta) {
    this.tipDokumenta = tipDokumenta
  }

  setTipRabata (tipRabata) {
    this.tipRabata = tipRabata
  }

  setTipPoreza (tipPoreza) {
    this.tipPoreza = tipPoreza
  }

  setBrojPovezanePrimke (brojPrimke) {
    this.povezanaPrimka = brojPrimke
  }

  setArtikl (artikl) {
    this.artikli.push(new Artikl(artikl))
  }

  setPorezi (porezi) {
    this.porezi = porezi
  }

  setManualArtikl (artikl, pdvIncluded) {
    // Pomoćna funkcija za sigurnu konverziju u broj (float)
    const safeParseFloat = (value, defaultValue = 0) => {
      if (value === null || value === undefined) return defaultValue
      // Ako je već broj, vrati ga (proveri i za NaN)
      if (typeof value === 'number') return isNaN(value) ? defaultValue : value
      // Ako je string, pokušaj da ga parsiraš
      if (typeof value === 'string') {
        // Zameni zarez tačkom i ukloni praznine
        const numericString = value.replace(',', '.').trim()
        // Ako je prazan string nakon čišćenja, vrati podrazumevanu vrednost
        if (numericString === '') return defaultValue
        const parsed = parseFloat(numericString)
        // Vrati parsiranu vrednost ako je validan broj, inače podrazumevanu
        return isNaN(parsed) ? defaultValue : parsed
      }
      // Za ostale tipove, pokušaj Number() konverziju
      const num = Number(value)
      return isNaN(num) ? defaultValue : num
    }

    var a = new Artikl(artikl) // Prvo inicijalizuj objekat

    if (pdvIncluded) {
      // --- Deo koda za slučaj kada cena UKLJUČUJE PDV ---
      // Potrebno je slično refaktorisanje i ovde
      const jedinicnaCijenaSPdvNum = safeParseFloat(artikl?.jedinicnaCijena)
      const stopaPdvNum = safeParseFloat(artikl?.stopaPdv) // Npr. 25 za 25%
      const ukupnaCijenaSPdvNum = safeParseFloat(artikl?.ukupnaCijena)

      // Izračunaj osnovnu cenu (bez PDV-a)
      // Pazi na deljenje nulom ako je stopaPdvNum -100 (ili 0 ako je stopa 0%)
      const divisor = 1 + (stopaPdvNum / 100)
      const cijenaBezPdv = divisor === 0 ? jedinicnaCijenaSPdvNum : jedinicnaCijenaSPdvNum / divisor
      const ukupnaCijenaBezPdv = divisor === 0 ? ukupnaCijenaSPdvNum : ukupnaCijenaSPdvNum / divisor

      // Postavi vrednosti koristeći settere (koji bi trebalo da budu ažurirani)
      a.setJedinicnaCijenaSPdv(jedinicnaCijenaSPdvNum)
      a.setJedinicnaCijenaBezPdv(cijenaBezPdv)
      a.setUkupnaCijenaSPdv(ukupnaCijenaSPdvNum)
      a.setUkupnaCijenaBezPdv(ukupnaCijenaBezPdv)
    } else {
      // --- Deo koda za slučaj kada cena NE UKLJUČUJE PDV ---
      const jedinicnaCijenaBezPdvNum = safeParseFloat(artikl?.jedinicnaCijena)
      const stopaPdvNum = safeParseFloat(artikl?.stopaPdv) // Npr. 25 za 25%
      const ukupnaCijenaBezPdvNum = safeParseFloat(artikl?.ukupnaCijena)

      // Izračunaj cenu sa PDV-om
      const multiplier = 1 + (stopaPdvNum / 100)
      const cijenaSaPdv = jedinicnaCijenaBezPdvNum * multiplier
      const ukupnaCijenaSPdv = ukupnaCijenaBezPdvNum * multiplier

      // Postavi vrednosti koristeći settere (koji bi trebalo da budu ažurirani)
      a.setJedinicnaCijenaBezPdv(jedinicnaCijenaBezPdvNum)
      a.setJedinicnaCijenaSPdv(cijenaSaPdv) // Ovde se prosleđuje izračunata vrednost
      a.setUkupnaCijenaBezPdv(ukupnaCijenaBezPdvNum)
      a.setUkupnaCijenaSPdv(ukupnaCijenaSPdv)
    }

    // Ostali setteri - proveri da li su već postavljeni u konstruktoru
    // ili ih treba eksplicitno postaviti/ažurirati ovde.
    // Posebno obrati pažnju na izračunavanje iznosa PDV-a.

    // Primer: Izračunaj i postavi iznos PDV-a na osnovu ukupnih cena
    // Pretpostavka: setteri za ukupne cene čuvaju vrednosti kao cele brojeve (centi)
    // Ako setteri primaju decimale, računaj sa decimalama pa prosledi setteru
    // const calculatedIznosPdv = (a.ukupnaCijenaSPdv / 100) - (a.ukupnaCijenaBezPdv / 100); // Ako su u centima
    const calculatedIznosPdv = safeParseFloat(a.ukupnaCijenaSPdv) - safeParseFloat(a.ukupnaCijenaBezPdv) // Ako su decimale pre settera
    a.setIznosPdv(calculatedIznosPdv) // Prosledi decimalnu vrednost setteru

    // Ovi su verovatno OK iz konstruktora, ali proveri
    // a.setStopaPdv(artikl?.stopaPdv ?? 0); // Možda treba safeParseFloat i ovde?
    // a.setRabat(artikl?.rabat ?? 0);
    // a.setMjernaJedinicaID(artikl?.mjernaJedinica ?? '');
    // a.setItem(artikl?.item ?? {});

    this.artikli.push(a)
  }

  getUkupnoPovratnaNaknada () {
    let povratnaNaknada = 0
    this.artikli.forEach((element) => {
      if (element.id === '-1') {
        povratnaNaknada += element.ukupnaCijenaSPdv
      }
    })
    return povratnaNaknada
  }
}

export default Artikl
